import { useCallback, useEffect, useState } from 'react'

import { useAbortController } from 'core/ui/hooks/useAbortController'
import { useAsync } from 'core/ui/hooks/useAsync'
import { useCookie } from 'core/ui/hooks/useCookie'

import user from 'legacy/utils/user'

import { useConfigContext } from 'config/ui/ConfigContext'

import { type PreferredTrackingMethod } from '../../domain/PreferredTrackingMethod'
import { getPreferredTrackingMethod as getPreferredTrackingMethodUseCase } from '../../use-cases/getPreferredTrackingMethod'
import { hasPreviousTracking as hasPreviousTrackingUseCase } from '../../use-cases/hasPreviousTracking'
import { updatePreferredTrackingMethod as updatePreferredTrackingMethodUseCase } from '../../use-cases/updatePreferredTrackingMethod'

const VIEWED_NOT_ONBOARDED_DIALOG_COOKIE_KEY = 'viewed-not-onboarded-dialog'
const VIEWED_ONBOARDED_DIALOG_COOKIE_KEY = 'viewed-onboarded-dialog'

export function usePreferredTrackingMethod() {
  const abortController = useAbortController()
  const configs = useConfigContext()
  const enableTrackingOnboarding = configs.enableTrackingOnboarding.toBoolean()
  const userIsLoggedIn = user.isLoggedIn()
  const [currentPreferred, setCurrentPreferred] = useState<PreferredTrackingMethod | undefined>()

  const [notOnboardedDialogViewed, setNotOnboardedDialogViewed] = useCookie<boolean>(
    VIEWED_NOT_ONBOARDED_DIALOG_COOKIE_KEY
  )
  const [onboardedDialogViewed, setOnboardedDialogViewed] = useCookie<boolean>(
    VIEWED_ONBOARDED_DIALOG_COOKIE_KEY
  )

  const {
    call: callHasPreviousTracking,
    result: hasPreviousTracking = false,
    isDone: idDonePrevious,
  } = useAsync(hasPreviousTrackingUseCase)

  const {
    call: callGetPreferredTrackingMethod,
    result: preferredTrackingMethod,
    isDone: isDonePreferred,
  } = useAsync(getPreferredTrackingMethodUseCase)

  const {
    call: callUpdatePreferredTrackingMethod,
    result: updatedPreferredTrackingMethod,
    isPending: isPendingUpdate,
    isDone: isDoneUpdate,
    hasError: hasErrorUpdate,
    reset: resetUpdatePreferredTrackingMethod,
  } = useAsync(updatePreferredTrackingMethodUseCase)

  const getHasPreviousTracking = useCallback(() => {
    callHasPreviousTracking(abortController)
  }, [abortController, callHasPreviousTracking])

  const getPreferredTrackingMethod = useCallback(() => {
    callGetPreferredTrackingMethod(abortController)
  }, [abortController, callGetPreferredTrackingMethod])

  const updatePreferredTrackingMethod = useCallback(
    (trackingMethod: PreferredTrackingMethod) => {
      callUpdatePreferredTrackingMethod(trackingMethod, abortController)
    },
    [abortController, callUpdatePreferredTrackingMethod]
  )

  const viewNotOnboardedDialog = useCallback(() => {
    setNotOnboardedDialogViewed(true)
  }, [setNotOnboardedDialogViewed])

  const viewOnboardedDialog = useCallback(() => {
    setOnboardedDialogViewed(true)
  }, [setOnboardedDialogViewed])

  useEffect(() => {
    if (enableTrackingOnboarding && userIsLoggedIn) {
      getHasPreviousTracking()
      getPreferredTrackingMethod()
    }
  }, [getHasPreviousTracking, getPreferredTrackingMethod, enableTrackingOnboarding, userIsLoggedIn])

  useEffect(() => {
    if (isDoneUpdate) {
      resetUpdatePreferredTrackingMethod()
      setCurrentPreferred(updatedPreferredTrackingMethod)
    }
  }, [isDoneUpdate, updatedPreferredTrackingMethod, resetUpdatePreferredTrackingMethod])

  useEffect(() => {
    if (isDonePreferred) setCurrentPreferred(preferredTrackingMethod)
  }, [isDonePreferred, preferredTrackingMethod])

  const areCallsDone = idDonePrevious && isDonePreferred

  // We should show the UserNotOnboardedDialog if the user is logged in,
  // has no previous tracking methods and no preferred tracking method.
  // If the user has already seen the dialog, we should not show it again.
  const showUserNotOnboardedDialog =
    enableTrackingOnboarding &&
    userIsLoggedIn &&
    !hasPreviousTracking &&
    !preferredTrackingMethod &&
    !notOnboardedDialogViewed &&
    areCallsDone

  // We should show the UserOnboardedDialog if the user is logged in,
  // and has previous tracking methods.
  // If the user has already seen the dialog, we should not show it again.
  const showUserOnboardedDialog =
    enableTrackingOnboarding &&
    userIsLoggedIn &&
    hasPreviousTracking &&
    !onboardedDialogViewed &&
    areCallsDone

  // We should show the UserNotOnboardedBanner if the user is logged in,
  // the user has no previous tracking methods, no preferred
  // tracking method, and the user has already seen the dialog.
  const showUserNotOnboardedBanner =
    enableTrackingOnboarding &&
    userIsLoggedIn &&
    !hasPreviousTracking &&
    !preferredTrackingMethod &&
    notOnboardedDialogViewed &&
    areCallsDone

  // We should show the UserOnboardedBanner if the user is logged in,
  // has previous tracking methods, no preferred tracking method,
  // and the user has already seen the dialog.
  const showUserOnboardedBanner =
    enableTrackingOnboarding &&
    userIsLoggedIn &&
    hasPreviousTracking &&
    !preferredTrackingMethod &&
    onboardedDialogViewed &&
    areCallsDone

  const showSignupUserNotOnboardedBanner = enableTrackingOnboarding && !userIsLoggedIn

  return {
    preferredTrackingMethod: currentPreferred,
    hasPreviousTracking,
    showUserNotOnboardedDialog,
    showUserOnboardedDialog,
    showUserNotOnboardedBanner,
    showUserOnboardedBanner,
    showSignupUserNotOnboardedBanner,
    isPendingUpdate,
    isDoneUpdate,
    hasErrorUpdate,
    viewNotOnboardedDialog,
    viewOnboardedDialog,
    updatePreferredTrackingMethod,
  }
}
